import type { Knowledge } from "~/interface/knowledge";

interface SampleProject {
  id: string;
  name: string;
  organization_id: string;
  description: string;
  created_at: string;
  modified_at: string;
  created_by: {
    id: string;
    first_name: string;
    last_name: string;
    display_name: string;
    email: string;
    organization_id: string;
  };
  outputs: Record<string, { id: string; isGenerating: boolean }>;
  estimationData: {
    ucList: unknown[];
    personMonths: number;
    amount: number;
  };
  vocabulary: Record<string, unknown>;
  meta_prompts: Record<string, unknown>;
}

export const sampleProject: SampleProject = {
  id: "tutorial-sample-project",
  name: "サンプルプロジェクト",
  organization_id: "tutorial-org",
  description: "REQXIGNの機能を体験するためのサンプルプロジェクトです",
  created_at: "2024-01-01T09:00:00Z",
  modified_at: "2024-01-20T12:00:00Z",
  created_by: {
    id: "tutorial-user",
    first_name: "チュートリアル",
    last_name: "ユーザー",
    display_name: "チュートリアルユーザー",
    email: "tutorial@example.com",
    organization_id: "tutorial-org",
  },
  outputs: {
    summary: {
      id: "tutorial-summary-001",
      isGenerating: false,
    },
    "system-proposal": {
      id: "tutorial-system-proposal-001",
      isGenerating: false,
    },
  },
  estimationData: {
    ucList: [],
    personMonths: 0,
    amount: 0,
  },
  vocabulary: {},
  meta_prompts: {},
};

export const sampleSummaryContent = `## 背景と課題
- 管理情報の散在と属人化
    - 根拠文
        - 「現状、顧客情報や進行中の案件、日々の営業活動がExcelや個人の手帳でバラバラに管理されていて、全体像が全く見えない状態なんです。誰がどの顧客に、どんな案件で、いつ、何をしたのかを一元的に可視化したいと考えています。」
        - 「情報の属人化を防ぎ、チーム全体で進捗を共有できる体制を作りたいということですね。」

## システム導入の目的:
- 営業支援システムによる情報統合と可視化
    - 根拠文
        - 「誰がどの顧客に、どんな案件で、いつ、何をしたのかを一元的に可視化したいと考えています。」
        - 「kintoneで3つのアプリを連携させる構成がシンプルで分かりやすいかと思います。」

## 決定事項:
- 3つのkintoneアプリ「顧客管理」「案件管理」「行動管理」を連携して導入する
    - 根拠文
        - 「まず、すべての情報の基盤となる『顧客管理』アプリを作成します。」
        - 「次に、具体的な商談を管理するための『案件管理』アプリを作成します。」
        - 「そして3つ目が、日々の営業活動を記録する『行動管理』アプリです。」
        - 「この3つのアプリを連携させることで、Aさんが当初仰っていた『誰が、どの顧客の、どの案件で、何をしたか』という情報の流れを、kintone上でスムーズに実現できます。」
        - 「ありがとうございます。この構成で進めたいです。」

## 次回までのタスク:
- 各アプリの必要項目（フィールド）のたたき台作成
    - 担当者: Bさん（受託企業）
    - 期限: 次回打ち合わせまで
    - 詳細: 「それぞれのアプリに具体的にどのような項目（フィールド）が必要か、弊社でたたき台を作成してまいります。」
    - 根拠文
        - 「では次回の打ち合わせまでに、それぞれのアプリに具体的にどのような項目（フィールド）が必要か、弊社でたたき台を作成してまいります。」

## 実現したい機能
- 顧客管理
    - 概要
        - 顧客の基本情報（会社名、部署、担当者名、連絡先）を登録し、顧客マスタとして一元管理する。
    - Actor
        - 営業担当者、システム管理者
    - 根拠文
        - 「まず、すべての情報の基盤となる『顧客管理』アプリを作成します。ここには、会社名、部署、担当者名、連絡先といった基本的な顧客情報を登録します。」

- 案件管理
    - 概要
        - 案件名、商談進捗（アポ、提案、クロージング等）、受注予定日、金額などの案件情報を管理し、顧客管理と紐付ける。
    - Actor
        - 営業担当者
    - 根拠文
        - 「具体的な商談を管理するための『案件管理』アプリを作成します。案件を登録する際に、『どの顧客の案件か』を顧客管理アプリから選択して登録する形です。」

- 行動管理
    - 概要
        - 訪問、電話、メール等の営業活動内容及び結果を記録し、案件管理および顧客管理アプリと紐づける。案件に紐づかない活動も顧客に直接紐づけ記録できる。
    - Actor
        - 営業担当者
    - 根拠文
        - 「日々の営業活動を記録する『行動管理』アプリです。訪問や電話、メールといった活動内容と、その結果を記録していきます。」
        - 「この『行動管理』アプリは、『案件管理』アプリと紐づけます。また、顧客管理とも直接紐づけておくことをお勧めします。」

## その他
- 利用者数
    - 社内ユーザー
        - 不明
    - 社外ユーザー
        - 不明
    - 根拠文
        - 記述なし

- データ件数
    - 不明
    - 根拠文
        - 記述なし

- 動作環境
    - 不明
    - 根拠文
        - 記述なし

- データ移行
    - 不明
    - 根拠文
        - 記述なし

- エラーについて
    - 不明
    - 根拠文
        - 記述なし

- インフラ基盤
    - 不明
    - 根拠文
        - 記述なし`;

export type SampleKnowledgeItem = {
  id: string;
  type: Knowledge["type"];
  title: string;
  summary: string;
  metaPrompt: string;
  updatedAt: string;
  status: "approved" | "pending" | "draft";
  highlights: string[];
  tags: string[];
};

export const sampleKnowledgeItems: SampleKnowledgeItem[] = [
  {
    id: "knowledge-system-overview",
    type: "system-proposal",
    title: "顧客管理システムの全体方針",
    summary:
      "顧客情報・案件情報を一元管理し、営業チーム全体の情報共有と意思決定を高速化するための基本方針です。",
    metaPrompt: `顧客管理システムのシステム概要書に追記するべき全体方針を、以下の観点で整理してください。
1. 目的（顧客情報・案件情報・営業活動ログの一元化）
2. 利用部門（営業・管理部門）
3. リリーススケジュールのマイルストーン
4. 現行課題との対比による導入メリット`,
    updatedAt: "2024-01-15T10:30:00Z",
    status: "approved",
    highlights: [
      "営業チーム内のリアルタイム共有を前提とした機能構成",
      "Excel運用からの移行時に想定されるリスクと対策",
      "システム導入後のモニタリング体制の簡易ロードマップ",
    ],
    tags: ["プロジェクト全体", "導入方針", "スケジュール"],
  },
  {
    id: "knowledge-functional-requirements",
    type: "functional-requirements",
    title: "営業案件管理のユースケース整理",
    summary:
      "商談登録から受注判定までの業務フローを基に、必要となる入力項目とバリデーションを定義しています。",
    metaPrompt: `機能要件書の営業案件管理セクションに、以下のユースケースを追記してください。
- 新規商談登録時に入力必須とする項目
- 案件ステータスごとに表示・編集が必要な情報
- 受注判定時に参照するレポート一覧`,
    updatedAt: "2024-01-18T09:15:00Z",
    status: "approved",
    highlights: [
      "案件進捗の可視化指標（確度ランク、見込み金額）の定義",
      "部門間で共通利用するデータ属性の洗い出し",
      "履歴管理に必要な最小限の入力項目セット",
    ],
    tags: ["営業管理", "案件ステータス", "入力項目"],
  },
  {
    id: "knowledge-non-functional",
    type: "non-functional-requirements",
    title: "パフォーマンス要件（営業繁忙期想定）",
    summary:
      "四半期末のアクセス集中を想定した同時接続数・レスポンス要求を整理し、Kintone標準機能での担保方針をまとめています。",
    metaPrompt: `非機能要件書に記載するパフォーマンス要件として、以下を具体化してください。
- 月末2営業日における想定同時接続数
- 応答時間の目標値と測定方法
- バックアップおよび障害復旧の基本方針`,
    updatedAt: "2024-01-19T13:05:00Z",
    status: "pending",
    highlights: [
      "同時50ユーザーアクセス時でも主要画面は3秒以内に表示",
      "1日1回の自動バックアップと七日間の世代管理",
      "アクセスログを利用した異常検知の一次対応フロー",
    ],
    tags: ["パフォーマンス", "BCP", "KPI"],
  },
  {
    id: "knowledge-app-design",
    type: "app-design",
    title: "Kintoneアプリ構造テンプレート",
    summary:
      "顧客・案件・活動履歴アプリのフィールド構成と、関連レコード一覧のレイアウト例を整理したテンプレートです。",
    metaPrompt: `アプリ設計書に掲載するフィールド構成サンプルを生成してください。
対象は『顧客アプリ』『案件アプリ』『活動履歴アプリ』の3種とし、各アプリの必須フィールド・データ型・表示形式を表形式でまとめてください。`,
    updatedAt: "2024-01-17T08:45:00Z",
    status: "approved",
    highlights: [
      "顧客アプリは7項目、案件アプリは9項目、活動履歴は6項目を標準搭載",
      "関連レコード一覧を用いた顧客起点の案件追跡フロー",
      "JavaScriptカスタマイズで拡張するフィールドの候補一覧",
    ],
    tags: ["フィールド設計", "関連レコード", "カスタマイズ"],
  },
  {
    id: "knowledge-customize-spec",
    type: "customize-specification",
    title: "メール連携カスタマイズの概要",
    summary:
      "案件ステータスの更新をトリガーに、社内ワークフローと顧客連絡を自動化するカスタマイズの基本仕様です。",
    metaPrompt: `カスタマイズ仕様書にまとめるべきメール連携の要件を作成してください。
- トリガーイベントと通知先の対応表
- メール本文テンプレートの差し込み項目
- 運用開始後の保守手順（テンプレート改修時の注意点）`,
    updatedAt: "2024-01-20T11:20:00Z",
    status: "draft",
    highlights: [
      "受注ステータス時に経理部へ自動通知、顧客にはフォローアップメール",
      "メールテンプレートはKintone管理画面から切り替え可能",
      "将来的な多言語対応を見据えた差し込み項目の分離設計",
    ],
    tags: ["メール連携", "ワークフロー", "運用"],
  },
];

export const sampleChatHistory = [
  {
    id: "chat-001",
    role: "user" as const,
    content: "このプロジェクトの主要な機能について教えてください",
    timestamp: new Date(Date.now() - 3600000).toISOString(),
  },
  {
    id: "chat-002",
    role: "assistant" as const,
    content:
      "このプロジェクトの主要な機能は以下の3つです：\n\n1. **顧客管理機能**\n   - 顧客の基本情報、取引履歴、営業活動履歴を一元管理します\n\n2. **案件管理機能**\n   - 案件の進捗状況、受注見込み、売上予測を管理します\n\n3. **レポート機能**\n   - 月次売上レポートや営業活動サマリーを自動生成します\n\nこれらの機能により、営業活動の効率化と売上予測の精度向上を実現します。",
    timestamp: new Date(Date.now() - 3500000).toISOString(),
  },
];

export const sampleSummaryDocument = {
  id: "tutorial-summary-001",
  name: "プロジェクトキックオフ議事録",
  type: "summary",
  content: sampleSummaryContent,
  meeting: {
    date: "2024-01-15T10:00:00Z",
    url: "",
  },
  sources: [],
  created_at: "2024-01-15T10:00:00Z",
  modified_at: "2024-01-15T11:30:00Z",
  created_by: {
    id: "tutorial-user",
    first_name: "チュートリアル",
    last_name: "ユーザー",
    display_name: "チュートリアルユーザー",
    email: "tutorial@example.com",
    organization_id: "tutorial-org",
  },
  modified_by: {
    id: "tutorial-user",
    first_name: "チュートリアル",
    last_name: "ユーザー",
    display_name: "チュートリアルユーザー",
    email: "tutorial@example.com",
    organization_id: "tutorial-org",
  },
};

export const sampleSystemProposalData = {
  営業活動統合管理システム: {
    システム概要: {
      概要: "",
      背景: [],
      目的: [
        "顧客情報、案件情報、行動履歴の統合管理による情報の可視化と共有",
        "属人化の防止と営業チーム全体での進捗共有体制の構築",
        "誰が、どの顧客に、どんな案件で、いつ、何をしたのかを一元的に可視化し、営業活動の全体像を把握すること",
      ],
      主な機能: {
        headers: ["機能", "概要"],
        rows: [
          ["顧客管理", "顧客の基本情報（会社名、部署、担当者名、連絡先）を登録し一元管理する機能"],
          [
            "案件管理",
            "商談案件の情報（案件名、進捗状況、受注予定日、金額）を管理し、顧客と紐づける機能",
          ],
          [
            "行動管理",
            "訪問、電話、メールなどの営業活動内容と結果を記録し、案件および顧客と紐づける機能",
          ],
        ],
      },
      メリット: [
        "営業情報の一元管理により業務効率が向上し、情報の見える化が実現できる",
        "属人化の解消により、誰がどの顧客にどんな営業活動を行っているかをチーム全体で共有可能",
        "kintoneアプリの活用で導入・運用がシンプルかつ柔軟に対応可能",
      ],
      その他: [
        "利用者数、データ件数、動作環境、データ移行手順、エラー対応、インフラ基盤については現時点で未確定",
      ],
      業務フロー: [
        "営業担当者 : 顧客情報の登録・更新 → 案件の登録・進捗状況の更新 → 営業活動（訪問、電話、メール等）の記録",
        "システム管理者 : システムの維持管理と運用監視",
      ],
      ユースケーステーブル: {
        headers: ["ユースケース", "ユースケース群", "アクター", "使用サービス", "実装方法"],
        rows: [
          ["顧客情報登録・更新", "顧客管理", "営業担当者", "kintone", "標準機能"],
          ["案件情報登録・進捗管理", "案件管理", "営業担当者", "kintone", "標準機能"],
          ["営業活動内容の記録", "行動管理", "営業担当者", "kintone", "標準機能"],
          ["システム維持管理", "システム管理", "システム管理者", "kintone", "標準機能"],
        ],
      },
    },
  },
};
